/**
 * Token Category Classification
 *
 * Categorizes trading pairs by token type for analytics grouping.
 * Supports multiple asset classes: crypto, stocks, forex.
 * Used for market filtering and Performance Lab Pro performance analytics.
 */

// =============================================================================
// Asset Class Types
// =============================================================================

export type AssetClass = "crypto" | "stocks" | "forex";

// Crypto-specific categories
export type CryptoCategory =
  | "major"    // BTC, ETH, SOL - Top market cap, high liquidity
  | "layer1"   // Alternative L1 chains
  | "layer2"   // L2 scaling solutions
  | "defi"     // DeFi protocols
  | "meme"     // Meme coins
  | "payment"  // Payment-focused tokens
  | "other";   // Uncategorized

// Stock-specific categories
export type StockCategory =
  | "technology"
  | "healthcare"
  | "finance"
  | "energy"
  | "consumer"
  | "industrial"
  | "other";

// Forex-specific categories
export type ForexCategory =
  | "major_pairs"
  | "minor_pairs"
  | "exotic"
  | "other";

// Combined token category type (for backward compatibility)
export type TokenCategory = CryptoCategory | StockCategory | ForexCategory;

// =============================================================================
// Category Mappings
// =============================================================================

/**
 * Market symbol to category mapping for crypto
 * Add new markets here as they're added to the platform
 */
export const TOKEN_CATEGORY_MAP: Record<string, CryptoCategory> = {
  // Major tokens (highest market cap, most liquid)
  "BTC-USD": "major",
  "ETH-USD": "major",
  "SOL-USD": "major",
  "BNB-USD": "major",

  // Layer 1 alternatives
  "ADA-USD": "layer1",
  "DOT-USD": "layer1",
  "AVAX-USD": "layer1",
  "NEAR-USD": "layer1",
  "ATOM-USD": "layer1",
  "FTM-USD": "layer1",
  "ALGO-USD": "layer1",
  "ICP-USD": "layer1",
  "HBAR-USD": "layer1",
  "VET-USD": "layer1",
  "EOS-USD": "layer1",
  "XTZ-USD": "layer1",
  "EGLD-USD": "layer1",
  "FLOW-USD": "layer1",
  "MINA-USD": "layer1",
  "KAS-USD": "layer1",
  "SEI-USD": "layer1",
  "SUI-USD": "layer1",
  "APT-USD": "layer1",
  "INJ-USD": "layer1",
  "TIA-USD": "layer1",

  // Layer 2 scaling solutions
  "MATIC-USD": "layer2",
  "ARB-USD": "layer2",
  "OP-USD": "layer2",
  "IMX-USD": "layer2",
  "STRK-USD": "layer2",
  "MNT-USD": "layer2",
  "METIS-USD": "layer2",
  "ZK-USD": "layer2",

  // Payment-focused tokens
  "XRP-USD": "payment",
  "LTC-USD": "payment",
  "BCH-USD": "payment",
  "XLM-USD": "payment",
  "DASH-USD": "payment",
  "ZEC-USD": "payment",

  // DeFi protocols
  "LINK-USD": "defi",
  "UNI-USD": "defi",
  "AAVE-USD": "defi",
  "MKR-USD": "defi",
  "CRV-USD": "defi",
  "SNX-USD": "defi",
  "COMP-USD": "defi",
  "LDO-USD": "defi",
  "RUNE-USD": "defi",
  "GMX-USD": "defi",
  "DYDX-USD": "defi",
  "1INCH-USD": "defi",
  "SUSHI-USD": "defi",
  "YFI-USD": "defi",
  "BAL-USD": "defi",
  "PENDLE-USD": "defi",
  "JUP-USD": "defi",
  "ONDO-USD": "defi",
  "ENA-USD": "defi",
  "PYTH-USD": "defi",
  "JTO-USD": "defi",
  "W-USD": "defi",

  // Meme coins
  "DOGE-USD": "meme",
  "SHIB-USD": "meme",
  "PEPE-USD": "meme",
  "FLOKI-USD": "meme",
  "BONK-USD": "meme",
  "WIF-USD": "meme",
  "MEME-USD": "meme",
  "BOME-USD": "meme",
  "BRETT-USD": "meme",
  "POPCAT-USD": "meme",
  "NEIRO-USD": "meme",
  "MOG-USD": "meme",
  "TURBO-USD": "meme",
};

/**
 * Get the category for a market symbol
 * Returns "other" for unknown markets
 */
export function getTokenCategory(market: string): TokenCategory {
  return TOKEN_CATEGORY_MAP[market] ?? "other";
}

// =============================================================================
// Labels by Asset Class
// =============================================================================

/**
 * Human-readable labels for crypto categories
 */
const CRYPTO_CATEGORY_LABELS: Record<CryptoCategory, string> = {
  major: "Major",
  layer1: "Layer 1",
  layer2: "Layer 2",
  defi: "DeFi",
  meme: "Meme",
  payment: "Payment",
  other: "Other",
};

/**
 * Human-readable labels for stock categories
 */
const STOCK_CATEGORY_LABELS: Record<StockCategory, string> = {
  technology: "Technology",
  healthcare: "Healthcare",
  finance: "Finance",
  energy: "Energy",
  consumer: "Consumer",
  industrial: "Industrial",
  other: "Other",
};

/**
 * Human-readable labels for forex categories
 */
const FOREX_CATEGORY_LABELS: Record<ForexCategory, string> = {
  major_pairs: "Major Pairs",
  minor_pairs: "Minor Pairs",
  exotic: "Exotic",
  other: "Other",
};

/**
 * Combined labels for backward compatibility
 */
const CATEGORY_LABELS: Record<TokenCategory, string> = {
  ...CRYPTO_CATEGORY_LABELS,
  ...STOCK_CATEGORY_LABELS,
  ...FOREX_CATEGORY_LABELS,
};

/**
 * Get the display label for a category
 */
export function getCategoryLabel(category: TokenCategory): string {
  return CATEGORY_LABELS[category] ?? category;
}

// =============================================================================
// Colors by Asset Class
// =============================================================================

/**
 * Category colors for crypto charts and visualizations
 * Uses semantic colors that work in both light and dark themes
 */
const CRYPTO_CATEGORY_COLORS: Record<CryptoCategory, string> = {
  major: "#f7931a",    // Bitcoin orange - Major assets
  layer1: "#627eea",   // Ethereum blue - L1 chains
  layer2: "#8b5cf6",   // Purple - L2 solutions
  defi: "#22c55e",     // Green - DeFi protocols
  meme: "#ec4899",     // Pink - Meme coins (distinct from Major orange)
  payment: "#06b6d4",  // Cyan - Payment tokens
  other: "#6b7280",    // Gray - Other
};

/**
 * Category colors for stock charts
 */
const STOCK_CATEGORY_COLORS: Record<StockCategory, string> = {
  technology: "#3b82f6",  // Blue - Tech
  healthcare: "#ef4444",  // Red - Healthcare
  finance: "#22c55e",     // Green - Finance
  energy: "#f59e0b",      // Amber - Energy
  consumer: "#8b5cf6",    // Purple - Consumer
  industrial: "#6b7280",  // Gray - Industrial
  other: "#9ca3af",       // Light gray - Other
};

/**
 * Category colors for forex charts
 */
const FOREX_CATEGORY_COLORS: Record<ForexCategory, string> = {
  major_pairs: "#22c55e", // Green - Major pairs
  minor_pairs: "#3b82f6", // Blue - Minor pairs
  exotic: "#f59e0b",      // Amber - Exotic
  other: "#6b7280",       // Gray - Other
};

/**
 * Combined colors for backward compatibility
 */
const CATEGORY_COLORS: Record<TokenCategory, string> = {
  ...CRYPTO_CATEGORY_COLORS,
  ...STOCK_CATEGORY_COLORS,
  ...FOREX_CATEGORY_COLORS,
};

/**
 * Get the chart color for a category
 */
export function getCategoryColor(category: TokenCategory): string {
  return CATEGORY_COLORS[category] ?? "#6b7280";
}

// =============================================================================
// Asset Class Helpers
// =============================================================================

/**
 * Get all categories for a specific asset class
 */
export function getCategoriesForAssetClass(assetClass: AssetClass): Array<{
  id: TokenCategory;
  label: string;
  color: string;
}> {
  switch (assetClass) {
    case "crypto":
      return (Object.keys(CRYPTO_CATEGORY_LABELS) as CryptoCategory[]).map((id) => ({
        id,
        label: CRYPTO_CATEGORY_LABELS[id],
        color: CRYPTO_CATEGORY_COLORS[id],
      }));
    case "stocks":
      return (Object.keys(STOCK_CATEGORY_LABELS) as StockCategory[]).map((id) => ({
        id,
        label: STOCK_CATEGORY_LABELS[id],
        color: STOCK_CATEGORY_COLORS[id],
      }));
    case "forex":
      return (Object.keys(FOREX_CATEGORY_LABELS) as ForexCategory[]).map((id) => ({
        id,
        label: FOREX_CATEGORY_LABELS[id],
        color: FOREX_CATEGORY_COLORS[id],
      }));
    default:
      return [];
  }
}

/**
 * Get all crypto categories with their labels and colors
 * Useful for building legends and filters (backward compatible)
 */
export function getAllCategories(): Array<{
  id: TokenCategory;
  label: string;
  color: string;
}> {
  return getCategoriesForAssetClass("crypto");
}

/**
 * Get markets by category from the static mapping
 */
export function getMarketsByCategory(category: CryptoCategory): string[] {
  return Object.entries(TOKEN_CATEGORY_MAP)
    .filter(([_, cat]) => cat === category)
    .map(([market]) => market);
}

/**
 * Asset class labels for display
 */
export const ASSET_CLASS_LABELS: Record<AssetClass, string> = {
  crypto: "Cryptocurrency",
  stocks: "Stocks",
  forex: "Forex",
};

/**
 * Get display label for asset class
 */
export function getAssetClassLabel(assetClass: AssetClass): string {
  return ASSET_CLASS_LABELS[assetClass];
}

/**
 * Get all asset classes with their labels
 */
export function getAllAssetClasses(): Array<{
  id: AssetClass;
  label: string;
}> {
  return (Object.keys(ASSET_CLASS_LABELS) as AssetClass[]).map((id) => ({
    id,
    label: ASSET_CLASS_LABELS[id],
  }));
}
